import { useState } from 'react';
import { api } from '../api/client';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { useT } from '../i18n/I18nProvider';

interface ExplainedPath {
  deviceId: string;
  deviceName: string;
  siteName: string | null;
  protocol: string;
  distance: number | null;
  metric: number | null;
  nextHop: string | null;
  interfaceName: string | null;
  areaId: string | null;
  active: boolean;
  present: boolean;
  rejectedReason: string | null;
  matchedPolicies: string[];
  ageSeconds: number;
  flapCount: number;
  lastFlapAt: string | null;
}

interface PrefixExplanation {
  prefix: string;
  origins: Array<{ deviceId: string; deviceName: string; reason: string }>;
  paths: ExplainedPath[];
  covering: string[];
  contained: string[];
  notes: string[];
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

/**
 * A route's state is three independent facts, so it gets three labels rather
 * than one: withdrawn (gone from the RIB), rejected (filtered by policy), and
 * active (installed in the FIB). A single status column would collapse cases an
 * operator needs to tell apart — a rejected route is a policy problem, a
 * withdrawn one is a reachability problem.
 */
function PathState({ path }: { path: ExplainedPath }): JSX.Element {
  const t = useT();
  if (!path.present) return <span className="chip">{t('routing.withdrawn')}</span>;
  if (path.rejectedReason)
    return <span className="chip chip--critical">{t('routing.rejected')}</span>;
  if (path.active) return <span className="chip chip--success">{t('routing.active')}</span>;
  return <span className="chip">{t('routing.backup')}</span>;
}

function PathTable({ paths }: { paths: ExplainedPath[] }): JSX.Element {
  const t = useT();
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('routing.device')}</th>
            <th>{t('routing.protocol')}</th>
            <th>{t('routing.path')}</th>
            <th>{t('routing.nextHop')}</th>
            <th>{t('routing.interface')}</th>
            <th>{t('routing.area')}</th>
            <th>{t('routing.distance')}</th>
            <th>{t('routing.metric')}</th>
            <th>{t('routing.age')}</th>
            <th>{t('routing.flaps')}</th>
            <th>{t('routing.policy')}</th>
          </tr>
        </thead>
        <tbody>
          {paths.map((p, i) => (
            <tr key={`${p.deviceId}-${p.protocol}-${p.nextHop ?? 'none'}-${i}`}>
              <td>
                {p.deviceName}
                {p.siteName ? <small> · {p.siteName}</small> : null}
              </td>
              <td>{p.protocol}</td>
              <td>
                <PathState path={p} />
                {p.rejectedReason ? <small> {p.rejectedReason}</small> : null}
              </td>
              <td>{p.nextHop ? <code>{p.nextHop}</code> : '—'}</td>
              <td>{p.interfaceName ?? '—'}</td>
              <td>{p.areaId ?? '—'}</td>
              <td>{p.distance ?? '—'}</td>
              <td>{p.metric ?? '—'}</td>
              <td>{formatAge(p.ageSeconds)}</td>
              <td>{p.flapCount > 0 ? p.flapCount : '—'}</td>
              <td>{p.matchedPolicies.length > 0 ? p.matchedPolicies.join('; ') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RouteExplorerPage(): JSX.Element {
  const t = useT();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<PrefixExplanation | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = (event: React.FormEvent): void => {
    event.preventDefault();
    const prefix = query.trim();
    if (!prefix) return;

    setLoading(true);
    setError(null);
    setSearched(true);
    api
      .get<PrefixExplanation>(`/routing/explorer/prefix?prefix=${encodeURIComponent(prefix)}`)
      .then((data) => setResult(data))
      .catch((err: unknown) => {
        setResult(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => setLoading(false));
  };

  return (
    <section>
      <div className="page-head">
        <h1>{t('routing.explorerTitle')}</h1>
      </div>

      <form className="form form--inline" onSubmit={search}>
        <label htmlFor="prefix-search">{t('routing.prefix')}</label>
        <input
          id="prefix-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('routing.searchPrefix')}
          autoComplete="off"
        />
        <button type="submit" className="btn" disabled={loading || query.trim() === ''}>
          {t('routing.explain')}
        </button>
      </form>

      {loading ? <Loading /> : null}
      {error ? <ErrorState message={error.message} /> : null}

      {!loading && !error && searched && result && result.paths.length === 0 ? (
        <EmptyState hint={t('routing.noResults')} />
      ) : null}

      {!loading && !error && result && result.paths.length > 0 ? (
        <>
          <h2>
            <code>{result.prefix}</code>
          </h2>

          {result.notes.length > 0 ? (
            <div className="banner">
              <h3>{t('routing.notes')}</h3>
              <ul>
                {result.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <h3>{t('routing.origins')}</h3>
          {result.origins.length === 0 ? (
            <p>{t('routing.noOrigin')}</p>
          ) : (
            <ul>
              {result.origins.map((o) => (
                <li key={o.deviceId}>
                  {o.deviceName} — {o.reason}
                </li>
              ))}
            </ul>
          )}

          <h3>{t('routing.path')}</h3>
          <PathTable paths={result.paths} />

          {result.covering.length > 0 ? (
            <>
              <h3>{t('routing.covering')}</h3>
              <p>
                {result.covering.map((c) => (
                  <code key={c}> {c} </code>
                ))}
              </p>
            </>
          ) : null}

          {result.contained.length > 0 ? (
            <>
              <h3>{t('routing.contained')}</h3>
              <p>
                {result.contained.map((c) => (
                  <code key={c}> {c} </code>
                ))}
              </p>
            </>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
